import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Area } from 'react-easy-crop';
import { authApi } from '../../api/auth.api';
import { apiErrorCode, getAccessToken } from '../../api/client';
import { useAuthStore } from '../../stores/auth.store';
import { UserAvatar } from '../ui/UserAvatar';
import { toast } from '../ui/Toast';
import { Camera, Icon } from '../ui/Icon';
import { cropToSquareJpeg, loadImage } from '../../utils/cropImage';
import { tooLargeMessage } from '../../utils/uploadLimits';
import type { User } from '../../types';

// The cropper only matters once someone picks a photo; in the main bundle it
// pushed the entry chunk past the PWA precache limit (vite.config.ts).
const ImageCropModal = lazy(() => import('../ui/ImageCropModal'));

const ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif';

export default function AvatarEditor() {
  const { t } = useTranslation();
  const { user, setAuth } = useAuthStore();
  const inputRef = useRef<HTMLInputElement>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => () => { if (cropSrc) URL.revokeObjectURL(cropSrc); }, [cropSrc]);

  if (!user) return null;
  const name = user.displayName || user.username;

  function applyUser(updated: User) {
    const token = getAccessToken();
    if (token) setAuth(updated, token);
  }

  function failureMessage(err: unknown): string {
    return apiErrorCode(err) === 'UNSUPPORTED_FILE_TYPE' || apiErrorCode(err) === 'IMAGE_DECODE_FAILED'
      ? t('account.avatar.unsupported')
      : t('account.avatar.failed');
  }

  async function upload(image: Blob, filename: string) {
    setSaving(true);
    try {
      applyUser(await authApi.uploadAvatar(image, filename));
      toast(t('account.avatar.saved'), 'success');
      setCropSrc(null);
    } catch (err) {
      toast(failureMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleFile(file: File) {
    const tooLarge = tooLargeMessage(file, 'media');
    if (tooLarge) { toast(tooLarge, 'error'); return; }

    const src = URL.createObjectURL(file);
    try {
      await loadImage(src);
      setCropSrc(src);
    } catch {
      // Only Safari decodes HEIC, which is what an iPhone hands over. The
      // server reads it and centres the square itself, so it goes up as is.
      URL.revokeObjectURL(src);
      await upload(file, file.name);
    }
  }

  async function handleCrop(area: Area) {
    if (!cropSrc) return;
    try {
      await upload(await cropToSquareJpeg(cropSrc, area), 'avatar.jpg');
    } catch {
      toast(t('account.avatar.failed'), 'error');
    }
  }

  async function handleRemove() {
    setSaving(true);
    try {
      applyUser(await authApi.removeAvatar());
      toast(t('account.avatar.removed'), 'success');
    } catch {
      toast(t('account.avatar.failed'), 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col items-center gap-1.5 shrink-0">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={saving}
        aria-label={user.avatarUrl ? t('account.avatar.change') : t('account.avatar.add')}
        className="relative group rounded-full cursor-pointer disabled:cursor-wait"
      >
        <UserAvatar key={user.avatarUrl ?? 'none'} name={name} avatarUrl={user.avatarUrl} size={56} />
        <span className="absolute -bottom-0.5 -right-0.5 w-6 h-6 rounded-full bg-teal-600 group-hover:bg-teal-700 text-white flex items-center justify-center ring-2 ring-white dark:ring-slate-900">
          <Icon as={Camera} size={12} />
        </span>
      </button>
      {user.avatarUrl && (
        <button
          type="button"
          onClick={handleRemove}
          disabled={saving}
          className="text-[11px] text-slate-400 hover:text-red-500 dark:text-slate-500 cursor-pointer"
        >
          {t('account.avatar.remove')}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={e => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void handleFile(file);
        }}
      />
      {cropSrc && (
        <Suspense fallback={null}>
          <ImageCropModal
            src={cropSrc} saving={saving} aspect={1} round
            title={t('account.avatar.cropTitle')} saveLabel={t('account.avatar.save')}
            onCancel={() => setCropSrc(null)} onConfirm={handleCrop}
          />
        </Suspense>
      )}
    </div>
  );
}
